// frontend/src/components/ABTestSection.jsx
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { listVariants, setVariants } from "../api/variants";
import { LABEL_CLS, INPUT_CLS } from "./QrPayloadFields";

/**
 * ABTestSection — "Enable A/B Testing" controls inside the code editor (P14-G8).
 *
 * Pro+ only: Free/Starter see a lock + upgrade prompt. When enabled, the user
 * edits two destinations and a single weight slider (variant B mirrors to
 * 100 − A). Saving replaces the code's variant set via the API. Existing
 * variants are loaded so an already-running test pre-fills the controls.
 *
 * @param {object} props
 * @param {number}  props.codeId       The code's id
 * @param {boolean} props.available    Whether the plan includes A/B testing
 */
function ABTestSection({ codeId, available }) {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [destA, setDestA] = useState("");
  const [destB, setDestB] = useState("");
  const [weightA, setWeightA] = useState(50);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!available) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    listVariants(codeId)
      .then((variants) => {
        if (cancelled) return;
        if (variants.length >= 2) {
          const [a, b] = variants;
          setEnabled(true);
          setDestA(a.destination);
          setDestB(b.destination);
          setWeightA(a.weight);
        }
      })
      .catch(() => {
        /* no variants / not allowed — leave defaults */
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [codeId, available]);

  const handleSave = async () => {
    if (!destA.trim() || !destB.trim()) {
      toast.error("Both A and B destinations are required.");
      return;
    }
    setSaving(true);
    try {
      await setVariants(codeId, [
        { name: "Variant A", destination: destA.trim(), weight: weightA },
        { name: "Variant B", destination: destB.trim(), weight: 100 - weightA },
      ]);
      toast.success("A/B test saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to save A/B test.");
    } finally {
      setSaving(false);
    }
  };

  // ── Plan gate ───────────────────────────────────────────────────────────
  if (!available) {
    return (
      <div className="border-t border-gray-100 pt-4">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center space-y-2">
          <p className="text-sm font-semibold text-gray-700 flex items-center justify-center gap-1.5">
            <svg
              className="w-4 h-4 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            A/B Testing — Pro feature
          </p>
          <p className="text-xs text-gray-500">
            Split traffic between two destinations and compare performance.
          </p>
          <a
            href="/billing"
            className="inline-block mt-1 px-4 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
          >
            Upgrade to Pro
          </a>
        </div>
      </div>
    );
  }

  if (loading) return null;

  return (
    <div className="border-t border-gray-100 pt-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-700">A/B Testing</p>
          <p className="text-xs text-gray-400">
            Route each scan to one of two destinations by weight
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            aria-label="Enable A/B Testing"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <div
            className="w-10 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600
            after:content-[''] after:absolute after:top-[2px] after:left-[2px]
            after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all
            peer-checked:after:translate-x-4"
          />
        </label>
      </div>

      {enabled && (
        <div className="space-y-3">
          <div>
            <label className={LABEL_CLS} htmlFor="ab-dest-a">
              Variant A destination ({weightA}%)
            </label>
            <input
              id="ab-dest-a"
              type="url"
              placeholder="https://a.example.com"
              className={INPUT_CLS}
              value={destA}
              onChange={(e) => setDestA(e.target.value)}
            />
          </div>
          <div>
            <label className={LABEL_CLS} htmlFor="ab-dest-b">
              Variant B destination ({100 - weightA}%)
            </label>
            <input
              id="ab-dest-b"
              type="url"
              placeholder="https://b.example.com"
              className={INPUT_CLS}
              value={destB}
              onChange={(e) => setDestB(e.target.value)}
            />
          </div>
          <div>
            <label className={LABEL_CLS} htmlFor="ab-weight">
              Traffic split — A {weightA}% / B {100 - weightA}%
            </label>
            <input
              id="ab-weight"
              type="range"
              min={1}
              max={99}
              value={weightA}
              onChange={(e) => setWeightA(Number(e.target.value))}
              className="w-full accent-blue-600"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save A/B test"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

ABTestSection.propTypes = {
  codeId: PropTypes.number.isRequired,
  available: PropTypes.bool.isRequired,
};

export default ABTestSection;
